import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '@clerk/clerk-react';
import AdminAPI from '../../utils/adminApi';

const CourseEditContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 2rem;
  margin: 0;
  font-weight: 700;
`;

const BackButton = styled.button`
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(102, 126, 234, 0.2);
  }
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr'};
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #667eea;
`;

const CheckboxLabel = styled.label`
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    }
  }
  
  &.secondary {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    border: 1px solid rgba(102, 126, 234, 0.2);
    
    &:hover {
      background: rgba(102, 126, 234, 0.2);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 12px;
  padding: 1rem;
  color: #e74c3c;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  background: rgba(46, 204, 113, 0.1);
  border: 1px solid rgba(46, 204, 113, 0.2);
  border-radius: 12px;
  padding: 1rem;
  color: #2ecc71;
  margin-bottom: 1rem;
`;

const CourseEdit = ({ courseId, onBack, onSave }) => {
  const { getToken } = useAuth();
  const [course, setCourse] = useState({
    title: '',
    description: '',
    price: '',
    currency: 'EUR',
    schedule_text: '',
    max_bookings: 10,
    category: '',
    billing_type: 'one_time',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      setError(null);
      let token;
      try {
        token = await getToken();
      } catch (err) {
        // Fallback für Development
        token = 'dev-clerk-token';
      }
      const response = await AdminAPI.getCourse(courseId, token);
      setCourse(response.data.course);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCourse(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const courseData = {
        ...course,
        price: parseFloat(course.price),
        max_bookings: parseInt(course.max_bookings)
      };

      let token;
      try {
        token = await getToken();
      } catch (err) {
        // Fallback für Development
        token = 'dev-clerk-token';
      }

      if (courseId) {
        await AdminAPI.updateCourse(courseId, courseData, token);
        setSuccess('Kurs erfolgreich aktualisiert!');
      } else {
        await AdminAPI.createCourse(courseData, token);
        setSuccess('Kurs erfolgreich erstellt!');
      }

      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CourseEditContainer>
        <LoadingSpinner>Kurs wird geladen...</LoadingSpinner>
      </CourseEditContainer>
    );
  }

  return (
    <CourseEditContainer>
      <Header>
        <Title>{courseId ? 'Kurs bearbeiten' : 'Neuer Kurs'}</Title>
        <BackButton onClick={onBack}>← Zurück</BackButton>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <Form onSubmit={handleSubmit}>
        <FormRow columns="1fr">
          <FormGroup>
            <Label>Titel *</Label>
            <Input
              name="title"
              value={course.title}
              onChange={handleInputChange}
              required
              placeholder="Kurstitel eingeben"
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>Beschreibung</Label>
          <TextArea
            name="description"
            value={course.description}
            onChange={handleInputChange}
            placeholder="Kursbeschreibung eingeben"
          />
        </FormGroup>

        <FormRow columns="1fr 1fr 1fr">
          <FormGroup>
            <Label>Preis (€) *</Label>
            <Input
              name="price"
              type="number"
              step="0.01"
              min="0.5"
              value={course.price}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Währung</Label>
            <Select
              name="currency"
              value={course.currency}
              onChange={handleInputChange}
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Max. Buchungen *</Label>
            <Input
              name="max_bookings"
              type="number"
              min="1"
              max="1000"
              value={course.max_bookings}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </FormRow>

        <FormRow columns="1fr 1fr">
          <FormGroup>
            <Label>Kategorie</Label>
            <Select
              name="category"
              value={course.category}
              onChange={handleInputChange}
            >
              <option value="">Keine Kategorie</option>
              <option value="ponyclub">Ponyclub</option>
              <option value="muki">Muki</option>
              <option value="heilpaedagogik">Heilpädagogik</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Abrechnungsart</Label>
            <Select
              name="billing_type"
              value={course.billing_type}
              onChange={handleInputChange}
            >
              <option value="one_time">Einmalig</option>
              <option value="subscription">Abonnement</option>
            </Select>
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>Zeitplan-Text</Label>
          <TextArea
            name="schedule_text"
            value={course.schedule_text}
            onChange={handleInputChange}
            placeholder="Zeitplan beschreiben (z.B. 'Jeden Dienstag 15:00-16:00')"
          />
        </FormGroup>

        <CheckboxGroup>
          <Checkbox
            name="is_active"
            type="checkbox"
            checked={course.is_active}
            onChange={handleInputChange}
          />
          <CheckboxLabel>Aktiv</CheckboxLabel>
        </CheckboxGroup>

        <ButtonGroup>
          <Button type="button" className="secondary" onClick={onBack}>
            Abbrechen
          </Button>
          <Button type="submit" className="primary" disabled={saving}>
            {saving ? 'Speichern...' : (courseId ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </ButtonGroup>
      </Form>
    </CourseEditContainer>
  );
};

export default CourseEdit;
